import { useState, useEffect, useRef } from "react";
import { requestJoin } from "../api/joinApi";
import { QRCodeCanvas } from "qrcode.react";

const BASE_URL = process.env.REACT_APP_API_URL || '/api';

export default function JoinPage({ branchCode, divisionCode }) {
  const [branches,     setBranches]     = useState([]);
  const [divisions,    setDivisions]    = useState([]);
  const [divisionSubList, setDivisionSubList] = useState([]);
  const [divisionPhone, setDivisionPhone] = useState('');
  const [showQrModal, setShowQrModal] = useState(false);
  const qrCanvasRef = useRef(null);
  
  const [form, setForm] = useState({
    branch_code: branchCode || '', division_code: divisionCode || '',
    name: '', emp_no: '', phone: '',
    division_sub_name: '', department: '', job_name: '', hope_msg: ''
  });
  const [loading,  setLoading]  = useState(false);
  const [done,     setDone]     = useState(false);
  const [error,    setError]    = useState('');

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // 지부 목록 로드
  useEffect(() => {
    fetch(`${BASE_URL}/branch`)
      .then(r => r.json())
      .then(data => { if (data.success) setBranches(data.data); });
  }, []);

  // 분회 목록 로드
  useEffect(() => {
    if (!form.branch_code) return;
    fetch(`${BASE_URL}/branch/${form.branch_code}/divisions`)
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setDivisions(data.data);
          if (divisionCode) {
            set('division_code', divisionCode);
            const found = data.data.find(d => d.division_code === divisionCode);
            setDivisionPhone(found?.phone || '');
          }
        }
      });
  }, [form.branch_code]);

  // 소속 목록 로드
  useEffect(() => {
    if (!form.branch_code || !form.division_code) { setDivisionSubList([]); return; }
    fetch(`${BASE_URL}/members/division-sub?branch_code=${form.branch_code}&division_code=${form.division_code}`)
      .then(r => r.json())
      .then(data => { if (data.success) setDivisionSubList(data.data); });
  }, [form.branch_code, form.division_code]);

const handleSubmit = async () => {
    setError('');
    if (!form.branch_code || !form.division_code) { setError('지부와 분회를 선택해주세요.'); return; }
    if (!form.name)              { setError('이름을 입력해주세요.'); return; }
    if (!form.emp_no)            { setError('사번을 입력해주세요.'); return; }
    if (!form.phone)             { setError('전화번호를 입력해주세요.'); return; }
    if (divisionSubList.length > 0 && !form.division_sub_name) { setError('소속을 입력해주세요.'); return; }
    if (!form.department)        { setError('부서를 입력해주세요.'); return; }
    if (!form.job_name)          { setError('직종을 입력해주세요.'); return; }

    setLoading(true);
    try {
      const res = await requestJoin(form);
      if (res.success) setDone(true);
      else setError(res.message);
    } catch (err) {
      setError('서버 연결에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 링크 복사
  const getJoinLink = () => `${window.location.origin}/?page=join&branch=${form.branch_code}&division=${form.division_code}`;

  // 복사 폴백
  const fallbackCopy = (text) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      document.execCommand('copy');
      alert('✅ 링크가 복사되었습니다!');
    } catch {
      alert('❌ 복사에 실패했습니다.');
    }
    document.body.removeChild(textarea);
  };

  // 링크 복사
  const handleCopyLink = () => {
    if (!form.branch_code || !form.division_code) {
      setError('지부와 분회를 먼저 선택해주세요.');
      return;
    }
    const url = getJoinLink();
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(url)
        .then(() => alert('✅ 링크가 복사되었습니다!'))
        .catch(() => fallbackCopy(url));
    } else {
      fallbackCopy(url);
    }
  };

  // QR 보기
  const handleShowQr = () => {
    if (!form.branch_code || !form.division_code) {
      setError('지부와 분회를 먼저 선택해주세요.');
      return;
    }
    setShowQrModal(true);
  };

  // QR다운로드
  const handleDownloadQr = () => {
    const canvas = qrCanvasRef.current?.querySelector('canvas');
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `가입신청QR_${form.branch_code}_${form.division_code}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  if (done) {
    return (
      <div style={styles.wrap}>
        <div style={styles.card}>
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <div style={{ fontSize: 60, marginBottom: 20 }}>✅</div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: "#1a1a2e", marginBottom: 12 }}>
              가입신청이 완료되었습니다!
            </h2>
            <p style={{ fontSize: 14, color: "#666", lineHeight: 1.8 }}>
              관리자 승인 후 로그인이 가능합니다.<br/>
              비밀번호는 전화번호 뒷 4자리입니다.
            </p>
            <button
              style={{ marginTop: 24, background: "#1677ff", color: "#fff", border: "none", borderRadius: 8, padding: "12px 32px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}
              onClick={() => window.location.href = '/'}>
              로그인 화면으로
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.wrap}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>✊</div>
          <h1 style={styles.title}>노동조합 가입신청</h1>
          <p style={styles.subtitle}>아래 정보를 입력하여 가입을 신청해주세요.</p>
        </div>

        <div style={styles.body}>
            {/* 지부/분회 */}
            <div style={styles.fieldGroup}>
                <div style={styles.field}>
                <label style={styles.label}>지부 <span style={{ color: "#ff4d4f" }}>*</span></label>
                <select style={styles.input} value={form.branch_code}
                    onChange={e => {
                      set('branch_code', e.target.value);
                      set('division_code', '');
                      setDivisionPhone('');
                    }}
                    disabled={!!branchCode}>
                    <option value="">지부 선택</option>
                    {branches.map(b => (
                    <option key={b.branch_code} value={b.branch_code}>{b.branch_name}</option>
                    ))}
                </select>
                </div>
                <div style={styles.field}>
                <label style={styles.label}>분회 <span style={{ color: "#ff4d4f" }}>*</span></label>
                <select style={styles.input} value={form.division_code}
                    onChange={e => set('division_code', e.target.value)}
                    disabled={!!divisionCode}>
                    <option value="">분회 선택</option>
                    {divisions.map(d => (
                    <option key={d.division_code} value={d.division_code}>{d.division_name}</option>
                    ))}
                </select>
                </div>
            </div>

            {/* 이름/사번 */}
            <div style={styles.fieldGroup}>
                <div style={styles.field}>
                <label style={styles.label}>이름 <span style={{ color: "#ff4d4f" }}>*</span></label>
                <input style={styles.input} value={form.name}
                    onChange={e => set('name', e.target.value)} placeholder="이름 입력" />
                </div>
                <div style={styles.field}>
                <label style={styles.label}>사번 <span style={{ color: "#ff4d4f" }}>*</span></label>
                <input style={styles.input} value={form.emp_no || ''}
                    onChange={e => set('emp_no', e.target.value)} placeholder="사번 입력" />
                </div>
            </div>

            {/* 전화번호 */}
            <div style={styles.field}>
                <label style={styles.label}>전화번호 <span style={{ color: "#ff4d4f" }}>*</span></label>
                <input style={styles.input} value={form.phone}
                onChange={e => set('phone', e.target.value)} placeholder="010-0000-0000" />
            </div>

            {/* 소속/부서 */}
            <div style={styles.fieldGroup}>
                <div style={styles.field}>
                  <label style={styles.label}>
                    소속 {divisionSubList.length > 0 && <span style={{ color: "#ff4d4f" }}>*</span>}
                    {divisionSubList.length > 0 && (
                      <span style={{ fontSize: 11, color: "#1677ff", fontWeight: 400, marginLeft: 6 }}>
                        예) {divisionSubList.slice(0, 3).map(s => s.division_sub_name).join(', ')}
                        {divisionSubList.length > 3 ? ' ...' : ''}
                      </span>
                    )}
                  </label>
                  <input style={{ ...styles.input, background: divisionSubList.length === 0 ? "#f0f0f0" : "#fafafa", color: divisionSubList.length === 0 ? "#aaa" : "#1a1a2e" }}
                    value={form.division_sub_name || ''}
                    onChange={e => set('division_sub_name', e.target.value)}
                    placeholder={divisionSubList.length === 0 ? "해당 분회는 소속 구분이 없습니다" : "소속 입력"}
                    disabled={divisionSubList.length === 0} />
                </div>
                <div style={styles.field}>
                <label style={styles.label}>부서 <span style={{ color: "#ff4d4f" }}>*</span></label>
                <input style={styles.input} value={form.department || ''}
                    onChange={e => set('department', e.target.value)} placeholder="부서 입력" />
                </div>
            </div>

            {/* 직종 */}
            <div style={styles.field}>
                <label style={styles.label}>직종 <span style={{ color: "#ff4d4f" }}>*</span></label>
                <input style={styles.input} value={form.job_name || ''}
                onChange={e => set('job_name', e.target.value)} placeholder="직종 입력" />
            </div>

            {/* 노동조합에 바라는 점 */}
            <div style={styles.field}>
                <label style={styles.label}>노동조합에 바라는 점</label>
                <textarea
                style={{ ...styles.input, resize: "vertical", minHeight: 80 }}
                value={form.hope_msg || ''}
                onChange={e => set('hope_msg', e.target.value)}
                placeholder="자유롭게 입력해주세요." />
            </div>

            {error && (
              <div style={styles.errorBox}>
                ⚠️ {error}
                {divisionPhone && (
                  <div style={{ marginTop: 6, fontSize: 12, color: "#888" }}>
                    문의: {divisionPhone}
                  </div>
                )}
              </div>
            )}

            <button
                style={{ ...styles.submitBtn, opacity: loading ? 0.7 : 1 }}
                onClick={handleSubmit}
                disabled={loading}>
                {loading ? '신청 중...' : '가입신청'}
            </button>

            <div style={{ textAlign: "center", marginTop: 12 }}>
              <a href="/" style={{ fontSize: 13, color: "#888", textDecoration: "none" }}>
                이미 회원이신가요? 로그인하기
              </a>
              {divisionPhone && (
                <div style={{ fontSize: 13, color: "#aaa", marginTop: 8 }}>
                  📞 가입문의 : {divisionPhone}
                </div>
              )}

              {/* 링크복사 / QR 뱃지 */}
              <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 14 }}>
                <span
                  style={{
                    display: "flex", alignItems: "center", gap: 4,
                    fontSize: 12, fontWeight: 600, padding: "6px 12px", borderRadius: 20,
                    background: form.branch_code && form.division_code ? "#f0f7ff" : "#f5f5f5",
                    color: form.branch_code && form.division_code ? "#1677ff" : "#bbb",
                    border: `1px solid ${form.branch_code && form.division_code ? "#d6e8ff" : "#eee"}`,
                    cursor: form.branch_code && form.division_code ? "pointer" : "not-allowed",
                    userSelect: "none",
                  }}
                  onClick={handleCopyLink}
                >
                  🔗 링크복사
                </span>
                <span
                  style={{
                    display: "flex", alignItems: "center", gap: 4,
                    fontSize: 12, fontWeight: 600, padding: "6px 12px", borderRadius: 20,
                    background: form.branch_code && form.division_code ? "#f0f7ff" : "#f5f5f5",
                    color: form.branch_code && form.division_code ? "#1677ff" : "#bbb",
                    border: `1px solid ${form.branch_code && form.division_code ? "#d6e8ff" : "#eee"}`,
                    cursor: form.branch_code && form.division_code ? "pointer" : "not-allowed",
                    userSelect: "none",
                  }}
                  onClick={handleShowQr}
                >
                  🔳 QR코드
                </span>
              </div>
            </div>
        </div>
      </div>
      {showQrModal && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000
        }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 28, width: 320, textAlign: "center" }}>
            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 4, color: "#1a1a2e" }}>
              가입신청 QR코드
            </div>
            <div style={{ fontSize: 12, color: "#888", marginBottom: 20 }}>
              {branches.find(b => b.branch_code === form.branch_code)?.branch_name} · {divisions.find(d => d.division_code === form.division_code)?.division_name}
            </div>
            <div ref={qrCanvasRef} style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
              <QRCodeCanvas value={getJoinLink()} size={200} />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                style={{ flex: 1, padding: "10px 0", borderRadius: 10, border: "1px solid #e0e0e0", background: "#fff", color: "#333", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                onClick={() => setShowQrModal(false)}
              >닫기</button>
              <button
                style={{ flex: 1, padding: "10px 0", borderRadius: 10, border: "none", background: "linear-gradient(135deg, #1677ff, #4096ff)", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
                onClick={handleDownloadQr}
              >💾 저장</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  wrap      : { minHeight: "100vh", background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 },
  card      : { background: "#fff", borderRadius: 20, width: "100%", maxWidth: 520, boxShadow: "0 20px 60px rgba(0,0,0,0.3)", overflow: "hidden" },
  header    : { background: "linear-gradient(135deg, #1677ff, #4096ff)", padding: "32px 24px", textAlign: "center" },
  title     : { fontSize: 24, fontWeight: 800, color: "#fff", margin: "0 0 6px" },
  subtitle  : { fontSize: 13, color: "rgba(255,255,255,0.8)", margin: 0 },
  body      : { padding: "28px 24px", display: "flex", flexDirection: "column", gap: 16 },
  fieldGroup: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  field     : { display: "flex", flexDirection: "column", gap: 6 },
  label     : { fontSize: 13, fontWeight: 600, color: "#444" },
  input     : { border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "10px 12px", fontSize: 14, outline: "none", background: "#fafafa", color: "#1a1a2e" },
  errorBox  : { background: "#fff5f5", border: "1px solid #ffcccc", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#c0392b" },
  submitBtn : { background: "linear-gradient(135deg, #1677ff, #4096ff)", color: "#fff", border: "none", borderRadius: 10, padding: "14px", fontSize: 15, fontWeight: 700, cursor: "pointer", marginTop: 4 },
};